import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Which container the table sits in. It decides the gap around the footer,
 * because the container owns the inset at the bottom:
 *
 *   page  — the table runs to the bottom of the screen, so the footer owns
 *           both gaps and uses the app gutter for each.
 *   card  — the table is inside a ContentCard, whose padding already supplies
 *           the gap beneath the footer, so the footer matches that padding
 *           above and adds nothing below.
 *
 * Getting this wrong is what makes a footer look bottom heavy: two owners
 * both adding space under the row.
 */
export type TableSurface = 'page' | 'card'

interface TableFooterProps {
  surface?: TableSurface
  className?: string
  children: React.ReactNode
}

/**
 * The channel beneath a table. The count, any pager, and the floating
 * selection action bar all live on this line — the bar finds this row by its
 * `data-table-footer` attribute and centres itself on it, so nothing has to
 * guess an offset.
 *
 * Its height is the `--spacing-table-footer` token, which the action bar also
 * uses as its minimum height; that is what keeps the two aligned. Pages must
 * not hand-roll this row: use TableFooter (or DataTablePagination, which wraps
 * it) so there is one definition to retune.
 */
export function TableFooter({ surface = 'page', className, children }: TableFooterProps) {
  return (
    <div
      data-table-footer
      className={cn(
        'flex items-center flex-wrap gap-3 ml-page-inset mr-0 flex-shrink-0 min-h-table-footer',
        surface === 'card'
          // Inside a card the gap is still the gutter, same as a full page
          // table. The card's padding is larger than that, so pull the extra
          // back with a negative margin — expressed from the two tokens, so
          // retuning either keeps the gaps equal.
          ? 'mt-gutter mb-[calc(var(--spacing-gutter)_-_var(--spacing-card-inset))]'
          : 'mt-gutter mb-gutter',
        className,
      )}
    >
      {children}
    </div>
  )
}
